import React from "react";
import Dropzone from "react-dropzone";

const INPUT_SHADOW =
  "0 1px 2px rgba(15, 23, 42, 0.05), 0 1px 3px rgba(15, 23, 42, 0.03)";

const styles = {
  slot: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
    minWidth: 0,
    padding: 14,
    borderRadius: 12,
    border: "1px solid #e8edf2",
    backgroundColor: "#f8fafc",
    boxShadow: INPUT_SHADOW,
  },
  label: {
    margin: 0,
    fontSize: 12,
    fontWeight: 600,
    color: "#475569",
    lineHeight: 1.35,
    wordBreak: "break-word" as const,
  },
  previewBox: {
    position: "relative" as const,
    width: "100%",
    height: 220,
    borderRadius: 10,
    border: "1px solid #e8edf2",
    backgroundColor: "#f1f5f9",
    overflow: "hidden",
    boxShadow: INPUT_SHADOW,
  },
  previewImg: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
    objectPosition: "center",
    cursor: "pointer",
    display: "block",
  },
  deleteBtn: {
    position: "absolute" as const,
    top: 8,
    right: 8,
    zIndex: 2,
    width: 32,
    height: 32,
    borderRadius: 999,
    border: "none",
    background: "rgba(220, 38, 38, 0.92)",
    color: "#ffffff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
  },
  previewEmpty: {
    position: "absolute" as const,
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 12,
  },
  dropzone: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 16,
    borderRadius: 10,
    border: "2px dashed #cbd5e1",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
  },
  dropzoneActive: {
    borderColor: "#50B7C0",
    backgroundColor: "#e8f7f8",
  },
  dropzoneText: {
    margin: 0,
    fontSize: 13,
    color: "#64748b",
    textAlign: "center" as const,
  },
  dropzoneHint: {
    margin: 0,
    fontSize: 11,
    color: "#94a3b8",
    textAlign: "center" as const,
  },
  replaceBtn: {
    marginTop: 4,
    padding: 0,
    border: "none",
    background: "none",
    color: "#50B7C0",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "center" as const,
    width: "100%",
  },
  emptyText: {
    margin: 0,
    fontSize: 13,
    color: "#94a3b8",
  },
};

type DocUploadSlotProps = {
  label: string;
  inputId: string;
  required?: boolean;
  file?: File | null;
  previewUrl?: string | null;
  canEdit?: boolean;
  onFileSelect: (file: File) => void | Promise<void>;
  onRemove?: () => void;
  onPreview: (src: string, title: string) => void;
};

const DocUploadSlot = ({
  label,
  inputId,
  required = false,
  file = null,
  previewUrl = null,
  canEdit = false,
  onFileSelect,
  onRemove,
  onPreview,
}: DocUploadSlotProps) => {
  const replaceInputRef = React.useRef<HTMLInputElement>(null);
  const objectUrl = React.useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return null;
  }, [file]);

  React.useEffect(() => {
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const src = objectUrl || previewUrl || null;
  const hasImage = Boolean(src);

  const handleFiles = async (accepted: File[]) => {
    if (!canEdit || !accepted.length) return;
    await onFileSelect(accepted[0]);
  };

  const handleReplaceChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (picked) await onFileSelect(picked);
    e.target.value = "";
  };

  return (
    <div style={styles.slot}>
      <span style={styles.label}>{label}</span>

      {canEdit && (
        <input
          type="file"
          id={inputId}
          accept="image/png,image/jpeg,image/jpg"
          required={required && !hasImage}
          style={{ display: "none" }}
          tabIndex={-1}
          aria-hidden
        />
      )}

      <div style={styles.previewBox}>
        {hasImage ? (
          <>
            {canEdit && onRemove ? (
              <button
                type="button"
                style={styles.deleteBtn}
                aria-label={`Quitar ${label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <i className="fa fa-trash" aria-hidden />
              </button>
            ) : null}
            <img
              src={src!}
              alt={label}
              style={styles.previewImg}
              onClick={() => onPreview(src!, label)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onPreview(src!, label);
                }
              }}
            />
          </>
        ) : canEdit ? (
          <Dropzone
            accept={{
              "image/png": [".png"],
              "image/jpeg": [".jpg", ".jpeg"],
            }}
            maxFiles={1}
            onDrop={handleFiles}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps()}
                style={{
                  ...styles.dropzone,
                  ...(isDragActive ? styles.dropzoneActive : {}),
                }}
              >
                <input {...getInputProps({ id: inputId })} />
                <i
                  className="fa fa-cloud-upload-alt"
                  style={{ fontSize: 28, color: "#50B7C0" }}
                  aria-hidden
                />
                <p style={styles.dropzoneText}>
                  Arrastra tu imagen aquí
                </p>
                <p style={styles.dropzoneHint}>o haz clic para seleccionar</p>
              </div>
            )}
          </Dropzone>
        ) : (
          <div style={styles.previewEmpty}>
            <p style={styles.emptyText}>Sin documento</p>
          </div>
        )}
      </div>

      {canEdit && hasImage ? (
        <>
          <input
            ref={replaceInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            style={{ display: "none" }}
            onChange={handleReplaceChange}
          />
          <button
            type="button"
            style={styles.replaceBtn}
            onClick={() => replaceInputRef.current?.click()}
          >
            Reemplazar imagen
          </button>
        </>
      ) : null}
    </div>
  );
};

export default DocUploadSlot;
